// Record the payer's answer to a request that is waiting on them.
//
// An outcome is written once — the server refuses a second one (409) — so the
// dialog says so before Save. An approval may carry the window and units the
// payer approved; both are optional, a blank date keeps the current one, and
// units merge into the lines (the disciplines named are set, the rest kept). A
// person's approval is applied as given, so the dialog says what will change.

import React, { useRef, useState } from "react";

import { OUTCOMES, OUTCOME_LABELS, recordOutcome } from "./api";
import { LinesEditor, parsedLines, validateLines } from "./DisciplineLines";

export default function OutcomeModal({
  practiceId,
  subject,
  onClose,
  onDone,
  onStale,
}) {
  const [outcome, setOutcome] = useState("");
  const [ref, setRef] = useState(subject.ref || "");
  const [note, setNote] = useState("");
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [units, setUnits] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busyRef.current) return;
    if (!outcome) {
      setError("Pick what the payer answered.");
      return;
    }
    if ((outcome === "denied" || outcome === "more_info") && !note.trim()) {
      setError("Say what the payer said — it is the reason shown when this comes back to the top.");
      return;
    }
    const approved = outcome === "approved";
    if (approved && validFrom && validTo && validTo < validFrom) {
      setError("The approved end date cannot be before its start date.");
      return;
    }
    if (approved) {
      const unitProblem = validateLines(units, { blankMeansNoCap: false });
      if (unitProblem) {
        setError(unitProblem);
        return;
      }
      // Blank does not mean "no cap" here: approved units merge into the lines,
      // and a blank amount keeps the cap on file. So a line must say a number.
      const blank = units.find((l) => String(l.authorized).trim() === "");
      if (blank) {
        setError(
          `Enter the amount the payer approved for ${blank.discipline}, or remove that line.`,
        );
        return;
      }
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await recordOutcome(practiceId, subject.authId, {
        outcome,
        ref,
        note,
        validFrom: approved ? validFrom : null,
        validTo: approved ? validTo : null,
        approvedUnits: approved ? parsedLines(units) : null,
      });
      const label = OUTCOME_LABELS[outcome].toLowerCase();
      onDone(
        outcome === "approved"
          ? `${subject.authNumber || "The authorization"} recorded as approved.`
          : `${subject.authNumber || "The authorization"} recorded as ${label}.`,
      );
    } catch (err) {
      setError(err.message || "Could not record the payer's answer.");
      // 409: already answered (or no longer pending). The list behind the dialog
      // still offers it, so it reloads.
      if (err.status === 409 && onStale) onStale();
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={busy ? undefined : onClose}>
      <div
        className="auth-modal auth-modal-small"
        role="dialog"
        aria-label="Record payer answer"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="auth-modal-header">
          <h3>Record the payer's answer</h3>
          <button
            type="button"
            className="auth-modal-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <form onSubmit={submit}>
          <div className="auth-modal-body">
            {error && (
              <div className="auth-message auth-message-error auth-message-inline">
                {error}
              </div>
            )}
            <p>
              {subject.authNumber || "This authorization"} for{" "}
              {subject.patientName || "this patient"}
              {subject.authority ? ` with ${subject.authority}` : ""}.
            </p>
            <div className="auth-field-row">
              <label className="auth-field">
                <span>Answer</span>
                <select
                  className="auth-input"
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value)}
                >
                  <option value="">Choose…</option>
                  {OUTCOMES.map((o) => (
                    <option key={o} value={o}>
                      {o === "withdrawn"
                        ? "Withdrawn — not asked after all, or marked by mistake"
                        : OUTCOME_LABELS[o]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="auth-field">
                <span>Payer reference</span>
                <input
                  className="auth-input"
                  value={ref}
                  onChange={(e) => setRef(e.target.value)}
                  maxLength={200}
                />
              </label>
            </div>
            {outcome === "approved" && (
              <>
                <div className="auth-field-row">
                  <label className="auth-field">
                    <span>Approved from</span>
                    <input
                      type="date"
                      className="auth-input"
                      value={validFrom}
                      onChange={(e) => setValidFrom(e.target.value)}
                    />
                  </label>
                  <label className="auth-field">
                    <span>Approved to</span>
                    <input
                      type="date"
                      className="auth-input"
                      value={validTo}
                      onChange={(e) => setValidTo(e.target.value)}
                    />
                  </label>
                </div>
                <p className="auth-field-hint">
                  Blank keeps the authorization's current date. A date entered here
                  replaces it.
                </p>
                <LinesEditor
                  lines={units}
                  onChange={setUnits}
                  title="Approved units by discipline"
                  emptyHint="None — the current units stay as they are. Add a discipline and enter the number the payer approved for it; disciplines not listed keep their current amount."
                  showUnit={false}
                />
              </>
            )}
            <label className="auth-field auth-field-full">
              <span>Note</span>
              <textarea
                className="auth-input auth-textarea"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder="What the payer said"
              />
            </label>
            <p className="auth-modal-note">
              An answer can be recorded only once, so check it before saving.
              To undo a Mark requested, record Withdrawn: the authorization
              comes off “Waiting on payer” and back to the advisor.
            </p>
          </div>
          <div className="auth-modal-footer">
            <button
              type="button"
              className="auth-btn"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="auth-btn auth-btn-primary"
              disabled={busy}
            >
              {busy ? "Saving…" : "Record answer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
